import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { LanguageKeys } from 'src/common/constants/languages';
import { SocialShareLocaleFields } from 'src/interfaces/SocialShare';

interface Props {
  locale: SocialShareLocaleFields;
  imageUrl?: string;
  siteName?: string;
  previewLang: LanguageKeys;
}

function PreviewCard({
  platformLabel,
  title,
  description,
  imageUrl,
  siteName,
}: {
  platformLabel: string;
  title: string;
  description: string;
  imageUrl?: string;
  siteName?: string;
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {platformLabel}
        </Typography>
      </CardContent>
      {imageUrl ? (
        <CardMedia
          component="img"
          image={imageUrl}
          alt=""
          sx={{ aspectRatio: '1200 / 630', objectFit: 'cover', width: '100%' }}
        />
      ) : null}
      <CardContent>
        {siteName ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {siteName}
          </Typography>
        ) : null}
        <Typography variant="subtitle2" component="div" sx={{ fontWeight: 600 }}>
          {title || '—'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description || '—'}
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function SocialSharePreviewCards({
  locale,
  imageUrl,
  siteName,
  previewLang,
}: Props) {
  const { t } = useTranslation();

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        {t('system.socialSharing.sharePreviewTitle')}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <PreviewCard
            platformLabel={t('system.socialSharing.previewFacebook')}
            title={locale.title}
            description={locale.description}
            imageUrl={imageUrl}
            siteName={siteName}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <PreviewCard
            platformLabel={t('system.socialSharing.previewWhatsApp')}
            title={locale.title}
            description={locale.description}
            imageUrl={imageUrl}
            siteName={siteName}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <PreviewCard
            platformLabel={t('system.socialSharing.previewX')}
            title={locale.title}
            description={locale.description}
            imageUrl={imageUrl}
            siteName={siteName}
          />
        </Grid>
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t('system.socialSharing.previewLangNote', { lang: previewLang.toUpperCase() })}
      </Typography>
    </Box>
  );
}
